/**
 * Edna role vocabulary — ordered, four values.
 *
 * This file MIRRORS the api-edna service's `app/authz/roles.py` and
 * `app/authz/route_roles.py`. It is not an independent policy: the service
 * enforces authorization on every request regardless of what this file says.
 * Gating exists so users are not shown controls that cannot work.
 *
 * **When the two disagree, the service is right and this file is wrong.**
 *
 *     standard  <  data_science  <  engineer  <  super_admin
 *
 * Two rules, and the asymmetry is deliberate — it matches the service:
 *
 * - **Reads are LENIENT.** `parseRole` resolves anything unrecognized, absent,
 *   or legacy to `standard` — the least privilege. It never throws, because the
 *   app shell reads a role on every page load and an exception there blanks the
 *   entire navigation.
 * - **Writes are STRICT.** Only the four values may be submitted. The UI
 *   constrains this by construction (a selector over `ROLE_ORDER`), so an
 *   invalid write is not expressible; the service independently rejects
 *   anything else with a 400.
 *
 * Fail-closed direction matters: an unknown value must resolve DOWN to
 * `standard`, never up. Resolving up would let a stray value confer privilege.
 */

/** An Edna role. Wire format is the lowercase string. */
export type EdnaRole = 'standard' | 'data_science' | 'engineer' | 'super_admin';

/**
 * The vocabulary in privilege order, least to most.
 *
 * Order is the source of truth for comparison — not alphabetical, not
 * incidental. Doubles as the option list for the role selector, so the UI
 * cannot offer a value the comparison does not understand.
 */
export const ROLE_ORDER: readonly EdnaRole[] = [
  'standard',
  'data_science',
  'engineer',
  'super_admin',
] as const;

/** Human-readable labels for display. Keep in sync with ROLE_ORDER. */
export const ROLE_LABELS: Record<EdnaRole, string> = {
  standard: 'Standard',
  data_science: 'Data Science',
  engineer: 'Engineer',
  super_admin: 'Super Admin',
};

/** The least-privileged role. What every fail-closed path resolves to. */
export const LEAST_PRIVILEGED: EdnaRole = 'standard';

/**
 * Resolve a value read from the API into a role. Never throws.
 *
 * Fail closed: anything unrecognized, null, undefined, or empty becomes
 * `standard`. The service's own read path does the same, including for its
 * legacy `admin` / `user` pair — note that legacy `admin` resolves to
 * `standard`, NOT to `super_admin`. That looks harsh and is intentional:
 * silently promoting on an unmigrated value would grant privilege from a value
 * we did not write. The service's data migration is what confers `super_admin`,
 * in one auditable place.
 */
export function parseRole(value: unknown): EdnaRole {
  if (typeof value !== 'string') return LEAST_PRIVILEGED;
  const normalized = value.trim().toLowerCase() as EdnaRole;
  return ROLE_ORDER.includes(normalized) ? normalized : LEAST_PRIVILEGED;
}

/**
 * Is `caller` at or above `minimum`?
 *
 * Ordered comparison, never equality. Equality gating is what broke at cutover:
 * `role === 'admin'` matched none of the four values, so privileged navigation
 * vanished silently. Equality against `'super_admin'` would work today and break
 * again the moment a threshold is tuned or a role is inserted between two
 * existing ones.
 */
export function atLeast(caller: EdnaRole, minimum: EdnaRole): boolean {
  return ROLE_ORDER.indexOf(caller) >= ROLE_ORDER.indexOf(minimum);
}

/**
 * Minimum role per gated capability — THE one table.
 *
 * Mirrored from the service's `ROUTE_ROLES` map, grouped by UI capability rather
 * than by route because that is how it is applied. Full route-by-route mapping:
 * `specs/001-api-edna-migration/contracts/role-gating.md`.
 *
 * Keep every threshold here. The service's route inventory records that the
 * `standard`/`data_science` boundary was deliberately left tunable, which is only
 * useful if retuning stays a one-table edit on this side too.
 *
 * ⚠️ `monitoring` and `userAdmin` are deliberately DIFFERENT. They were a single
 * `adminOnly` flag before, which is why that flag could not express the
 * requirement — gating user administration at `engineer` would show it to
 * engineers whose every request then 403s.
 *
 * Note what is absent: no entry for reads. Every read in the service's map is
 * `standard`, i.e. any authenticated user. Gating a read more tightly than the
 * service does removes function for no security benefit.
 */
export const GATES = {
  /** Create / update / delete on clients, matters, projects, models, vectors. */
  hierarchyWrite: 'data_science',
  /**
   * Brandwatch sample + register; LexisNexis validate, preview, register.
   * Sample/validate/preview persist nothing but DO spend third-party provider
   * quota, which is why the service puts them above a plain read.
   */
  datasourceWrite: 'data_science',
  /**
   * Pipeline launch / terminate / delete. Stricter than the monolith, on
   * purpose. Status and datasource polling stay ungated — the service keeps them
   * at `standard` so Air Traffic Control keeps working for everyone watching it.
   */
  pipelineControl: 'engineer',
  /** The whole Monitoring screen — all three of its routes require this. */
  monitoring: 'engineer',
  /** User list and role assignment. */
  userAdmin: 'super_admin',
} as const satisfies Record<string, EdnaRole>;

/** A gated capability name. */
export type Gate = keyof typeof GATES;
